/**
 * Browser error reports for Watch. The payload is only message, stackHash,
 * urlPath, buildId, and ts. Query strings, emails, tokens, Authorization
 * values, Stripe ids, and homeowner fields are removed before the report
 * is sent, logged, or stored. The stack itself is never sent.
 */

const LIVE_PROJECT_REF = 'zvnubjrizoiwrzrhunjz';
const DEV_PROJECT_REF = 'ftrhhpaduufkjfkjwzru';
const RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_MESSAGE = 300;
const CLOCK_SKEW_MS = 5 * 60 * 1000;
const CLIENT_LIMIT = 8;
const CLIENT_WINDOW_MS = 60 * 1000;
const DEDUPE_MS = 30 * 1000;

const HOMEOWNER_KEYS = [
  'first_name',
  'last_name',
  'full_name',
  'email_normalized',
  'password_hash',
  'invite_code',
  'homeowner_account_id',
  'stripe_customer_id',
  'stripe_subscription_id',
  'zip_code',
  'home_key',
  'address',
  'street',
  'phone',
  'email',
  'name',
  'city',
  'state',
  'zip',
  'password',
].join('|');

function fieldPattern() {
  return new RegExp(
    '(^|[^A-Za-z0-9_])("|\')?(' + HOMEOWNER_KEYS + ')\\2?\\s*[:=]\\s*',
    'gi'
  );
}

function supabaseUrl(env) {
  const source = env || {};
  return String(source.SUPABASE_URL || source.REACT_APP_SUPABASE_URL || '');
}

function isLiveSupabaseUrl(env) {
  return supabaseUrl(env).indexOf(LIVE_PROJECT_REF) !== -1;
}

function stackFingerprint(stack) {
  const text = String(stack || '');
  return fnv1a(text, 2166136261) + fnv1a(text, 2166136261 ^ 0x9e3779b9);
}

function fnv1a(text, seed) {
  let hash = seed >>> 0;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function readFieldValue(text, start) {
  const rest = text.slice(start);
  if (!rest) return '';
  const quote = rest[0];
  if (quote === '"' || quote === "'") {
    let index = 1;
    while (index < rest.length) {
      if (rest[index] === '\\') {
        index += 2;
        continue;
      }
      if (rest[index] === quote) return rest.slice(0, index + 1);
      index += 1;
      if (index > 200) break;
    }
    return rest.slice(0, Math.min(rest.length, 200));
  }
  const nextField = new RegExp(
    '(?:^|[^A-Za-z0-9_])(?:"|\')?(?:' + HOMEOWNER_KEYS + ')(?:"|\')?\\s*[:=]',
    'i'
  );
  const found = rest.slice(1).search(nextField);
  let end = found === -1 ? rest.length : found + 1;
  end = Math.min(end, 160);
  const urlAt = rest.search(/https?:\/\//i);
  if (urlAt > 0 && urlAt < end) end = urlAt;
  const stop = rest.slice(0, end).search(/[,;&]/);
  if (stop !== -1) end = stop;
  return rest.slice(0, end).replace(/\s+$/, '');
}

function redactHomeownerFields(text) {
  const pattern = fieldPattern();
  let out = '';
  let last = 0;
  let match = pattern.exec(text);
  while (match) {
    const value = readFieldValue(text, pattern.lastIndex);
    out += text.slice(last, match.index) + match[1] + match[3] + '=[redacted]';
    last = pattern.lastIndex + value.length;
    pattern.lastIndex = last;
    match = pattern.exec(text);
  }
  return out + text.slice(last);
}

function sanitizeClientErrorText(value) {
  if (typeof value !== 'string') return '';
  let text = value.replace(/\s+/g, ' ').trim().slice(0, 2000);
  if (!text) return '';
  text = text.replace(/\?[^ \t\r\n#]*/g, '');
  text = text.replace(/#(?:access_token|token|id_token|code)=[^\s]*/gi, '');
  text = text.replace(/#[A-Za-z0-9._~+/=-]{24,}/g, '');
  text = redactHomeownerFields(text);
  text = text.replace(/\b(?:authorization|x-homeowner-token|access_token|refresh_token|id_token|api_key|apikey)\b\s*[:=]\s*\S+/gi, '[redacted]');
  text = text.replace(/\bBearer\s+[A-Za-z0-9._~+/=-]{8,}/gi, 'Bearer [redacted]');
  text = text.replace(/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g, '[redacted]');
  text = text.replace(/\b(?:sk|pk|rk)_(?:live|test)_[A-Za-z0-9]+\b/g, '[redacted]');
  text = text.replace(/\bwhsec_[A-Za-z0-9]+\b/g, '[redacted]');
  text = text.replace(/\b(?:cus|sub|pi|pm|price|prod|seti|acct|ch|txn|src|card|tok|py|evt|cs|in)_[A-Za-z0-9]*\d[A-Za-z0-9]*\b/g, '[redacted]');
  text = text.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted]');
  text = text.replace(/(^|[^A-Za-z0-9])((?:\+?1[-.\s]?)?(?:\(\d{3}\)|\d{3})[-.\s]\d{3}[-.\s]\d{4})(?![A-Za-z0-9])/g, '$1[redacted]');
  text = text.replace(/\b[A-Fa-f0-9]{32,}\b/g, '[redacted]');
  text = text.replace(/\b[A-Za-z0-9+/_-]{40,}={0,2}\b/g, '[redacted]');
  text = text.replace(/\s+/g, ' ').trim().slice(0, MAX_MESSAGE);
  if (!text) return '[redacted]';
  return text;
}

function safeUrlPath(value) {
  if (typeof value !== 'string') return '/';
  let raw = value.trim();
  if (!raw) return '/';
  for (let pass = 0; pass < 2; pass += 1) {
    const hashAt = raw.indexOf('#');
    if (hashAt !== -1) raw = raw.slice(0, hashAt);
    const queryAt = raw.indexOf('?');
    if (queryAt !== -1) raw = raw.slice(0, queryAt);
    if (pass === 0 && /^[a-z][a-z0-9+.-]*:/i.test(raw)) {
      try {
        raw = new URL(raw).pathname || '/';
      } catch (err) {
        return '/';
      }
    }
    try {
      raw = decodeURIComponent(raw);
    } catch (err) {
      return '/';
    }
  }
  if (raw.indexOf('@') !== -1 || raw.indexOf('\\') !== -1 || raw.indexOf('?') !== -1 || /\s/.test(raw)) {
    return '/';
  }
  if (!raw.startsWith('/')) raw = '/' + raw;
  const parts = raw.split('/');
  for (let index = 0; index < parts.length; index += 1) {
    if (parts[index] === '..') return '/';
  }
  if (raw.length > 200) raw = raw.slice(0, 200);
  if (!/^\/[A-Za-z0-9._~!$&'()*+,;=%/-]*$/.test(raw)) return '/';
  return raw || '/';
}

function safeBuildId(value) {
  if (typeof value !== 'string') return 'unknown';
  const trimmed = value.trim();
  if (!/^[A-Za-z0-9._-]{1,40}$/.test(trimmed)) return 'unknown';
  if (/(?:sk|pk|rk|whsec|cus|sub|pi|pm|price|prod|seti|acct|ch|cs)_/i.test(trimmed)) return 'unknown';
  if (/^eyJ/i.test(trimmed)) return 'unknown';
  return trimmed;
}

function clientBuildId(env) {
  const source = env || {};
  const configured = source.REACT_APP_BUILD_ID;
  if (typeof configured === 'string' && configured.trim()) return safeBuildId(configured);
  return 'dev';
}

function normalizeStackHash(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim().toLowerCase();
  if (/^[a-f0-9]{16}$/.test(trimmed) || /^[a-f0-9]{64}$/.test(trimmed)) return trimmed;
  return '';
}

function safeTimestamp(value, now) {
  const clock = typeof now === 'number' ? now : Date.now();
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.trunc(value);
  if (rounded < clock - RETENTION_MS || rounded > clock + CLOCK_SKEW_MS) return null;
  return rounded;
}

function acceptClientError(body, now) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const keys = Object.keys(body).sort();
  if (keys.join(',') !== 'buildId,message,stackHash,ts,urlPath') return null;
  if (typeof body.message !== 'string' || !body.message.trim()) return null;
  const message = sanitizeClientErrorText(body.message);
  const stackHash = normalizeStackHash(body.stackHash);
  if (!message || !stackHash) return null;
  const ts = safeTimestamp(body.ts, now);
  if (ts === null) return null;
  return {
    message,
    stackHash,
    urlPath: safeUrlPath(body.urlPath),
    buildId: safeBuildId(body.buildId),
    ts,
  };
}

function reportFields(input) {
  const source = input || {};
  const message = sanitizeClientErrorText(source.message) || '[redacted]';
  return {
    message,
    stackHash: stackFingerprint(source.stack || message),
    urlPath: safeUrlPath(source.pathname),
    buildId: safeBuildId(source.buildId || 'dev'),
    ts: source.now,
  };
}

function reportsEnabled(location, env, native) {
  if (native) return false;
  const host = String((location && location.hostname) || '').replace(/^\[|\]$/g, '').toLowerCase();
  if (host === 'pacarion.com' || host === 'www.pacarion.com' || host === 'app.pacarion.com') return false;
  if (isLiveSupabaseUrl(env)) return false;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return true;
  if (host.endsWith('.netlify.app') && host.indexOf('--') !== -1) return true;
  if (supabaseUrl(env).indexOf(DEV_PROJECT_REF) !== -1) return true;
  return false;
}

function postReport(fetchImpl, payload) {
  try {
    const result = fetchImpl('/.netlify/functions/report-client-error', {
      method: 'POST',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (result && typeof result.catch === 'function') result.catch(function () {});
  } catch (err) {
    // A failed report is dropped. It must not throw into the page.
  }
}

function noteAssistantFailure(where, detail, consoleImpl) {
  const place = String(where || 'assistant').replace(/[^a-z0-9-]/gi, '').slice(0, 40) || 'assistant';
  const reason = String(detail || 'request failed')
    .replace(/\s+/g, ' ')
    .replace(/\b(?=[A-Z0-9.\-]*\d)[A-Z0-9][A-Z0-9.\-]{5,}\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  const error = new Error(`Assistant ${place} failed: ${reason}`);
  const target = consoleImpl || console;
  if (target && typeof target.error === 'function') target.error(error);
  return error;
}

function installClientErrorReporter(options) {
  const settings = options || {};
  const target = settings.window || (typeof window !== 'undefined' ? window : null);
  if (!target || target.__pacarionClientErrorReporter) return { enabled: false };
  if (typeof target.addEventListener !== 'function') return { enabled: false };
  const env = settings.env || (typeof process !== 'undefined' ? process.env : {});
  const enabled = typeof settings.enabled === 'boolean'
    ? settings.enabled
    : reportsEnabled(target.location, env, settings.native);
  if (!enabled) return { enabled: false };
  target.__pacarionClientErrorReporter = true;

  const fetchImpl = settings.fetch || target.fetch;
  const buildId = settings.buildId || clientBuildId(env);
  const clock = typeof settings.now === 'function' ? settings.now : function () { return Date.now(); };
  const recent = [];
  const seen = new Map();
  let reporting = false;

  function allowSend(at) {
    while (recent.length && at - recent[0] >= CLIENT_WINDOW_MS) recent.shift();
    if (recent.length >= CLIENT_LIMIT) return false;
    recent.push(at);
    return true;
  }

  function sendReport(message, stack) {
    if (reporting) return;
    if (String(stack || '').indexOf('installClientErrorReporter') !== -1) return;
    const at = clock();
    const payload = reportFields({
      message: typeof message === 'string' ? message : '',
      stack: typeof stack === 'string' ? stack : '',
      pathname: target.location && target.location.pathname,
      buildId,
      now: at,
    });
    const prior = seen.get(payload.stackHash) || 0;
    if (at - prior < DEDUPE_MS) return;
    if (!allowSend(at)) return;
    seen.set(payload.stackHash, at);
    reporting = true;
    try {
      postReport(fetchImpl, payload);
    } finally {
      reporting = false;
    }
  }

  target.addEventListener('error', function (event) {
    const err = event && event.error;
    const message = (err && typeof err.message === 'string' && err.message)
      || (event && typeof event.message === 'string' && event.message)
      || 'error';
    const stack = err && typeof err.stack === 'string' ? err.stack : '';
    sendReport(message, stack);
  });

  target.addEventListener('unhandledrejection', function (event) {
    const reason = event && event.reason;
    if (reason instanceof Error) {
      sendReport(reason.message, reason.stack || '');
      return;
    }
    if (typeof reason === 'string') {
      sendReport(reason, '');
      return;
    }
    sendReport('Unhandled rejection', '');
  });

  const original = target.console && target.console.error;
  if (typeof original === 'function') {
    target.console.error = function pacarionConsoleError() {
      original.apply(target.console, arguments);
      if (reporting) return;
      const parts = [];
      let stack = '';
      for (let index = 0; index < arguments.length; index += 1) {
        const arg = arguments[index];
        if (typeof arg === 'string') parts.push(arg);
        else if (arg instanceof Error) {
          parts.push((arg.name || 'Error') + ': ' + (arg.message || ''));
          if (!stack && typeof arg.stack === 'string') stack = arg.stack;
        } else parts.push('[omitted]');
      }
      sendReport(parts.join(' '), stack);
    };
  }

  return { enabled: true };
}

module.exports = {
  LIVE_PROJECT_REF,
  DEV_PROJECT_REF,
  RETENTION_MS,
  CLIENT_LIMIT,
  sanitizeClientErrorText,
  safeUrlPath,
  safeBuildId,
  clientBuildId,
  stackFingerprint,
  normalizeStackHash,
  safeTimestamp,
  acceptClientError,
  reportFields,
  reportsEnabled,
  isLiveSupabaseUrl,
  installClientErrorReporter,
  noteAssistantFailure,
};
